// src/components/common/InvoiceRefundStatus.js — refundstatus-8a3d6f12 (R1d-3)
// The two small pieces the invoice screen shows on a refund line made by the new refund flow:
//   RefundQbStatus  — where the refund stands in QuickBooks, with Retry (Owner / Admin / Manager;
//                     only they can read refund records, so only they are given this piece)
//   CreditNoteLine  — whether the credit note was emailed, with Resend (same roles) and Download
//                     (anyone in the company)
// The wording and the reading of replies live in utils/invoiceRefundStatus.js. The server gates
// every call; its refusals are shown word for word.
import React, { useRef, useState } from 'react';
import { CheckCircle, AlertTriangle, Clock, RotateCcw, Loader2, Download, Mail } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import { formatDate, toJsDate } from '../../utils/formatters';
import {
    refundQbStatus, creditNoteEmailStatus, buildRetryPayload, readRetryReply, readResendReply, readCallError, retryBankPreset,
} from '../../utils/invoiceRefundStatus';
import { downloadPdfBytes } from '../../utils/pdfDownload'; // docpdf-8a4c1e73
import SentDocumentCopies from './SentDocumentCopies'; // docpdf-8a4c1e73 — saved copies of the emails (office only)

const TONE = { green: 'text-green-600', red: 'text-red-600', amber: 'text-amber-700', gray: 'text-gray-500' };
const link = 'inline-flex items-center gap-1 text-xs font-medium disabled:opacity-50';
const Note = ({ reply }) => (reply ? <span role="status" className={`text-xs ${reply.ok ? 'text-green-700' : 'text-red-600'}`}>{reply.message}</span> : null);

export const RefundQbStatus = ({ rec, invoiceId, refundId, companySettings }) => {
    const [panel, setPanel] = useState(false);
    const [banks, setBanks] = useState({ status: 'idle', list: [], error: '' });
    const [bankId, setBankId] = useState('');
    const [busy, setBusy] = useState(false);
    const [reply, setReply] = useState(null);
    const sendingRef = useRef(false);
    const status = refundQbStatus(rec);
    if (!status) return <span className="text-gray-400">—</span>;

    const run = async (chosenBank) => {
        if (sendingRef.current) return;
        sendingRef.current = true;
        setBusy(true); setReply(null);
        try {
            const res = await httpsCallable(functions, 'retryInvoiceRefundSync')(buildRetryPayload({ invoiceId, refundId, bankAccountId: chosenBank, canChangeBank: status.canChangeBank }));
            setReply(readRetryReply(res?.data));
            setPanel(false);
        } catch (e) {
            setReply({ ok: false, message: readCallError(e, 'QuickBooks could not be retried. Please try again.') });
        } finally {
            sendingRef.current = false;
            setBusy(false);
        }
    };
    const startRetry = async () => {
        setReply(null);
        if (!status.canChangeBank) { run(''); return; } // already partly in QuickBooks: the account can't change
        setPanel(true);
        setBanks({ status: 'loading', list: [], error: '' });
        try {
            const res = await httpsCallable(functions, 'getQuickBooksBankAccounts')({ bankOnly: true });
            const list = Array.isArray(res?.data?.accounts) ? res.data.accounts : [];
            setBanks({ status: 'ready', list, error: '' });
            setBankId(retryBankPreset(rec, companySettings, list));
        } catch (e) {
            setBanks({ status: 'error', list: [], error: readCallError(e, 'QuickBooks could not be reached.') });
        }
    };
    const Icon = status.tone === 'green' ? CheckCircle : status.tone === 'gray' ? Clock : AlertTriangle;
    const bankOwed = status.needsBank && banks.status === 'ready' && banks.list.length > 0 && !bankId;

    return (
        <div className="flex flex-col items-end gap-1 max-w-xs ml-auto">
            <span className={`${TONE[status.tone]} flex items-center justify-end gap-1`}><Icon size={14} /> {status.label}</span>
            {status.detail && <span className="text-xs text-red-600 text-right">{status.detail}</span>}
            {status.note && <span className="text-xs text-gray-500 text-right">{status.note}</span>}
            {status.canRetry && !panel && (
                <button onClick={startRetry} disabled={busy} className={`${link} text-[#5F4EC9] hover:underline`}>
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} Retry in QuickBooks
                </button>
            )}
            {panel && (
                <div className="w-full border rounded-md p-2 space-y-2 text-left bg-gray-50">
                    <label htmlFor={`retry-bank-${refundId}`} className="block text-xs font-medium text-gray-700">Bank account the refund comes from</label>
                    {banks.status === 'loading' && <p className="text-xs text-gray-500 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Loading your bank accounts…</p>}
                    {banks.status === 'ready' && banks.list.length > 0 && (
                        <select id={`retry-bank-${refundId}`} value={bankId} onChange={(e) => setBankId(e.target.value)} className="w-full border border-gray-300 rounded-md px-2 py-1 text-xs">
                            <option value="">-- Choose a bank account --</option>
                            {banks.list.map((a) => <option key={a.id} value={String(a.id)}>{a.name}</option>)}
                        </select>
                    )}
                    {(banks.status === 'error' || (banks.status === 'ready' && banks.list.length === 0)) && (
                        <p className="text-xs text-amber-700">
                            {banks.status === 'error' ? `Your bank accounts couldn't be loaded from QuickBooks (${banks.error}).` : 'QuickBooks returned no bank accounts.'}
                            {' '}Retry will use the Refund-from Bank Account in Settings → Integrations.
                        </p>
                    )}
                    <div className="flex justify-end gap-3">
                        <button onClick={() => setPanel(false)} disabled={busy} className={`${link} text-gray-600`}>Cancel</button>
                        <button onClick={() => run(bankId)} disabled={busy || banks.status === 'loading' || bankOwed} className={`${link} text-[#5F4EC9] hover:underline`}>
                            {busy ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} Retry now
                        </button>
                    </div>
                </div>
            )}
            <Note reply={reply} />
        </div>
    );
};

// orgId (docpdf-8a4c1e73): lists the saved copies of this credit note's emails (Office Staff and up).
export const CreditNoteLine = ({ creditNoteId, creditNoteNumber, creditNote, canSend, orgId }) => {
    const [busy, setBusy] = useState('');
    const [reply, setReply] = useState(null);
    const sendingRef = useRef(false);
    const status = creditNoteEmailStatus(creditNote, (ts) => formatDate(toJsDate(ts)));

    const send = async () => {
        if (sendingRef.current) return;
        sendingRef.current = true;
        setBusy('send'); setReply(null);
        try {
            const res = await httpsCallable(functions, 'resendCreditNoteEmail')({ creditNoteId });
            setReply(readResendReply(res?.data));
        } catch (e) {
            setReply({ ok: false, message: readCallError(e, 'The credit note could not be emailed. Please try again.') });
        } finally {
            sendingRef.current = false;
            setBusy('');
        }
    };
    // docpdf-8a4c1e73 — getCreditNotePdf returns the PDF's bytes; saved in this browser (nothing stored, no link).
    const download = async () => {
        if (sendingRef.current) return;
        sendingRef.current = true;
        setBusy('download'); setReply(null);
        try {
            const res = await httpsCallable(functions, 'getCreditNotePdf', { timeout: 120000 })({ creditNoteId });
            downloadPdfBytes(res?.data?.pdfBase64, res?.data?.fileName);
        } catch (e) {
            setReply({ ok: false, message: `Could not download the credit note. ${readCallError(e, 'Unknown error')}` });
        } finally {
            sendingRef.current = false;
            setBusy('');
        }
    };

    return (
        <div className="mt-1 flex flex-col gap-1">
            {status && <span className={`text-xs ${TONE[status.tone]}`}>{status.text}</span>}
            <span className="flex flex-wrap items-center gap-3">
                {canSend && status && status.canSend && (
                    <button onClick={send} disabled={!!busy} aria-label={`${status.sendLabel} credit note ${creditNoteNumber || ''}`.trim()} className={`${link} text-[#5F4EC9] hover:underline`}>
                        {busy === 'send' ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />} {status.sendLabel}
                    </button>
                )}
                <button onClick={download} disabled={!!busy} aria-label={`Download credit note ${creditNoteNumber || ''}`.trim()} className={`${link} text-[#5F4EC9] hover:underline`}>
                    {busy === 'download' ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Download
                </button>
            </span>
            <Note reply={reply} />
            <SentDocumentCopies orgId={orgId} kind="creditNote" docId={creditNoteId} />
        </div>
    );
};
